import { Skeleton } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { CpWidgetVariantType } from 'api/customer-portal/types';
import { CpSectionWrapper } from 'components/organisms/customer-portal/widgets/common';
import { CpLoadingProps, TitleAndDesc } from '../common';

const FaqSkeleton = styled(Skeleton.Button)<{
  small?: boolean;
}>`
  .ant-skeleton,
  &.ant-skeleton,
  .ant-skeleton-button,
  &.ant-skeleton-button,
  & {
    max-width: 100% !important;
    width: 100% !important;
    height: ${(props) => (props?.small ? '80px' : '160px')} !important;
  }
  margin-top: var(--cp-spacing-06);

  .ant-skeleton-button {
    border-radius: var(--border-radius-default);
  }
`;

const GridItemWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    gap: var(--cp-spacing-03);
  }
`;

const FaqAccordion: React.FC = () => {
  return (
    <>
      <FaqSkeleton active shape="square" small />
      <FaqSkeleton active shape="square" small />
      <FaqSkeleton active shape="square" small />
    </>
  );
};

const FaqRow: React.FC = () => {
  return (
    <>
      <FaqSkeleton active shape="round" />
      <FaqSkeleton active shape="round" />
      <FaqSkeleton active shape="round" />
    </>
  );
};

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 24px 48px;
  gap: var(--cp-spacing-06);

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    grid-gap: 0;
  }
`;

const Question = styled(Skeleton.Button)`
  .ant-skeleton,
  &.ant-skeleton,
  .ant-skeleton-button,
  &.ant-skeleton-button,
  & {
    max-width: 100% !important;
    width: 100% !important;
    height: 32px !important;
  }
  margin-top: var(--cp-spacing-03);

  .ant-skeleton-button {
    border-radius: var(--border-radius-default);
  }
`;

const Answer = styled(Skeleton.Button)<{
  isFirstChild?: boolean;
}>`
  .ant-skeleton,
  &.ant-skeleton,
  .ant-skeleton-button,
  &.ant-skeleton-button,
  & {
    max-width: 100% !important;
    width: 100% !important;
    height: 24px !important;
  }
  margin-top: ${(props) =>
    props?.isFirstChild ? 'var(--cp-spacing-05)' : 'var(--cp-spacing-05)'};

  .ant-skeleton-button {
    border-radius: var(--border-radius-default);
  }
`;

const GridItem: React.FC = () => {
  return (
    <GridItemWrapper>
      <Question active shape="round" />
      <Question active shape="round" />

      <Answer active shape="round" isFirstChild />
      <Answer active shape="round" />
      <Answer active shape="round" />
      <Answer active shape="round" />
    </GridItemWrapper>
  );
};

const FaqGrid: React.FC = () => {
  return (
    <GridWrapper>
      <GridItem />
      <GridItem />
      <GridItem />
    </GridWrapper>
  );
};

const Content: React.FC<{ variant: CpWidgetVariantType }> = ({ variant }) => {
  switch (variant) {
    case CpWidgetVariantType.FAQAccordion:
      return <FaqAccordion />;
    case CpWidgetVariantType.FAQRow:
      return <FaqRow />;
    case CpWidgetVariantType.FAQGrid:
      return <FaqGrid />;
    default:
      return <></>;
  }
};

export const CpFAQLoading: React.FC<CpLoadingProps> = ({
  input,
  styles,
  variantType,
}) => {
  return (
    <CpSectionWrapper
      background={styles?.background as string}
      paddingY={styles?.paddingY as number | undefined}
      className="!cp-max-w-none"
    >
      <TitleAndDesc
        desc={input?.description as string}
        title={input?.title as string}
      />

      <Content variant={variantType} />
    </CpSectionWrapper>
  );
};
